import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import * as yup from 'yup';

import {
  useMinRequiredMobileVersionGet,
  useUpdateMinRequiredMobileVersion,
} from '@sbv-clients/feature-facilities';
import { Form, FormTextField, FormValues } from '@sbv-clients/ui-forms';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActionsContainer,
  DialogContent,
  DialogHeader,
  Typography,
  useConfirmationModal,
} from '@sbv-clients/ui-shared';
import { ActionLabels, getApiErrorMessage } from '@sbv-clients/util-misc';

const schema = yup.object({
  minRequiredVersion: yup
    .string()
    .trim()
    .required()
    .matches(/^\d+\.\d+\.\d+$/, 'Enter a version number like 5.0.2')
    .label('Minimum required version')
    .default(''),
});

type MobileAppVersionFormValues = FormValues<typeof schema>;

const FormBody = ({
  currentVersion,
  onCancel,
}: {
  currentVersion: string;
  onCancel: () => void;
}) => {
  const { formState } = useFormContext();
  const { isSubmitting, isDirty } = formState;
  return (
    <>
      <DialogContent>
        <Box display="grid" gridTemplateColumns="1fr" gap={4}>
          <Typography color="text.secondary">
            Users running an older version of the mobile app will see a
            force-update screen.
            {currentVersion
              ? ` Current minimum: ${currentVersion}.`
              : ' No minimum is set.'}
          </Typography>
          <FormTextField
            fullWidth
            name="minRequiredVersion"
            type="text"
            label="Minimum required version"
            placeholder="5.0.2"
          />
        </Box>
      </DialogContent>
      <DialogActionsContainer>
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          {ActionLabels.Cancel}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isSubmitting || !isDirty}
        >
          {ActionLabels.Save}
        </Button>
      </DialogActionsContainer>
    </>
  );
};

interface MobileAppVersionDialogProps {
  open: boolean;
  onClose: () => void;
}

export const MobileAppVersionDialog = ({
  open,
  onClose,
}: MobileAppVersionDialogProps) => {
  const { data, isLoading, isError, error } = useMinRequiredMobileVersionGet({
    enabled: open,
  });
  const updateVersion = useUpdateMinRequiredMobileVersion();
  const { modal, openModal } = useConfirmationModal();

  const currentVersion = data?.minRequiredVersion ?? '';
  const defaultValues = useMemo(
    () => ({ minRequiredVersion: currentVersion }),
    [currentVersion]
  );

  const onSubmit = useCallback(
    async ({ minRequiredVersion }: MobileAppVersionFormValues) => {
      try {
        await openModal({
          title: 'Change minimum required version?',
          content: (
            <Typography>
              Mobile users on a version lower than{' '}
              <strong>{minRequiredVersion}</strong> will be forced to update
              before they can use the app. Make sure this version is already
              released in the App Store and Google Play, or no one will be able
              to use the app.
            </Typography>
          ),
        });
      } catch {
        return;
      }
      try {
        await updateVersion.mutateAsync(minRequiredVersion);
        onClose();
        // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    [openModal, updateVersion, onClose]
  );

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="xs">
        <DialogHeader title="Mobile App Version" onClose={onClose} />
        {isLoading && (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Box p={4}>
            <Alert severity="error">{getApiErrorMessage(error)}</Alert>
          </Box>
        )}
        {!isLoading && !isError && (
          <Form
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            mode="onTouched"
            resetOnDefaultChanges
          >
            <FormBody currentVersion={currentVersion} onCancel={onClose} />
          </Form>
        )}
      </Dialog>
      {modal}
    </>
  );
};
